import { useMemo } from 'react';
import { useAutocompleteTheme } from '../code-editor/editor-theme';
import { PromQLExtension } from './codemirror';
import { HTTPPrometheusClient, type PrometheusConfig } from './codemirror/client/prometheus';
import { promQLMetricMetadataLinterExtension } from './codemirror/extensions/promql-metric-linter';
import { promQLTooltipExtension } from './codemirror/extensions/promql-tooltip';

export interface UsePromQLExtensionsOptions {
  queryApiEndpoint?: string;
  enableLinter?: boolean;
  timeRange?: PrometheusConfig['timeRange'];
  enableBuiltInVariables?: boolean;
}

/**
 * shared promql codemirror extensions: autocomplete theme, completion via the
 * prometheus http client, optional linter (with tooltip + metadata extensions),
 * and built-in variable completion. consumers like PromQLEditor and unified
 * QueryEditor.Input pass the result straight to CodeEditor's `extensions` prop.
 */
export const usePromQLExtensions = ({
  queryApiEndpoint = '/data/m3',
  enableLinter = true,
  timeRange,
  enableBuiltInVariables = false,
}: UsePromQLExtensionsOptions = {}) => {
  const autocompleteTheme = useAutocompleteTheme();

  return useMemo(() => {
    const client = new HTTPPrometheusClient({
      url: queryApiEndpoint,
      timeRange,
    });

    const exts = [
      autocompleteTheme,
      new PromQLExtension()
        .activateLinter(enableLinter)
        .setComplete({ remote: client, enableBuiltInVariables })
        .asExtension(),
    ];

    if (enableLinter) {
      exts.push(promQLTooltipExtension({ client: client.metadataClient }));
      exts.push(promQLMetricMetadataLinterExtension({ client: client.metadataClient }));
    }

    return exts;
  }, [queryApiEndpoint, timeRange, autocompleteTheme, enableLinter, enableBuiltInVariables]);
};
